/**
 * Ad-hoc Food Safety logging helpers (Cooking / Cooling / Deliveries).
 * Same language as fridges: ENTER temp → SUBMIT → ready for next.
 */

import {
  FOOD_SAFETY_ADHOC_SECTION_META,
  FOOD_SAFETY_ADHOC_TEMP_DEFAULTS,
  FOOD_SAFETY_DELIVERY_BAND_KEYWORDS,
  FOOD_SAFETY_DELIVERY_LEGACY_MINS,
  FOOD_SAFETY_DELIVERY_TEMP_BAND,
  FOOD_SAFETY_DELIVERY_TEMP_PRESETS,
  FOOD_SAFETY_QUICK_PICK_CHIP_MAX,
  FOOD_SAFETY_RESPONSE_TYPE,
  FOOD_SAFETY_SECTION,
} from 'src/constants/foodSafety'
import { formatFoodSafetyHeldLine } from 'src/utils/foodSafetyHoldingRange'
import {
  EMPTY_FOOD_SAFETY_CORRECTIVE_DRAFT,
  buildFoodSafetyCorrectiveActionBody,
} from 'src/utils/foodSafetyCorrectiveAction'
import { formatCoolingLogExtras } from 'src/utils/foodSafetyCooling'
import {
  defaultDeliveryRejectionScope,
  deliveryBandsPayload,
  formatDeliveryBands,
  formatDeliveryRejection,
  isDeliveryDropOutOfRange,
  validateDeliveryDrop,
} from 'src/utils/foodSafetyDeliveryDrop'
import { isFridgeTempOutOfRange } from 'src/utils/foodSafetyFridges'
import {
  formatFoodSafetyCorrectiveNote,
  formatFoodSafetyTempParts,
} from 'src/utils/foodSafetyTempColour'
import { formatFoodSafetyClockTime } from 'src/utils/foodSafetyTime'
import {
  formatFoodSafetyTempRange,
  formatTempValue,
} from 'src/utils/foodSafetyTempRange'

/** Saved foods on the log screen. Chips when they exist; a Setup link when they don't. */
export const FOOD_SAFETY_SAVED_FOOD_HEADING = 'Tap a saved food'
export const FOOD_SAFETY_COMPOSER_SELECT_SAVED = 'Select saved'
export const FOOD_SAFETY_COMPOSER_TYPE_NEW = 'Type a new name'
export const FOOD_SAFETY_SAVE_AS_TEMPLATE = 'Save as template'
export const FOOD_SAFETY_SAVE_AS_TEMPLATE_DONE = 'Saved in Setup'
export const FOOD_SAFETY_SAVE_FOODS_LINE =
  'Save them once — chicken, soups, batches, the proteins that always arrive.'
export const FOOD_SAFETY_SAVE_FOODS_LINK = 'Save foods in Setup'
/** Beside Recent OMS deliveries: tap list for a drop that was not an OMS order. */
export const FOOD_SAFETY_STANDING_DELIVERY_HEADING = 'Not an OMS order'
export const FOOD_SAFETY_DELIVERY_LIST_PRODUCT_HINT =
  'Record the products sampled; a supplier delivery record does not mean every product was temperature-checked.'
export const FOOD_SAFETY_DELIVERY_SAMPLING_GUIDANCE =
  'When checking deliveries, select a representative range of high-risk products. Vary the products checked over time rather than selecting the same items on every delivery.'
export const FOOD_SAFETY_DELIVERY_RECEIPT_FILLS_LINE =
  'An OMS receipt fills itself in when you receipt it.'
export const FOOD_SAFETY_DELIVERY_EMPTY_LINE = `${FOOD_SAFETY_SAVE_FOODS_LINE} ${FOOD_SAFETY_DELIVERY_RECEIPT_FILLS_LINE}`

/** Delivery decision chips (29.64) — equal weight Accept / Reject, not a quiet Switch. */
export const FOOD_SAFETY_DELIVERY_ACCEPT_LABEL = 'Accepted'
export const FOOD_SAFETY_DELIVERY_REJECT_LABEL = 'Rejected'
export const FOOD_SAFETY_DELIVERY_REJECT_HINT =
  'Say why it went back. A short note is optional.'

/** Deliveries marked Rejected — the reason is the corrective action. */
export function isAdHocDeliveryRejected(section, draft) {
  return section === FOOD_SAFETY_SECTION.DELIVERIES && draft?.accepted === false
}

export function getAdHocSectionMeta(section) {
  return FOOD_SAFETY_ADHOC_SECTION_META[section] || null
}

export function isAdHocSection(section) {
  return Boolean(FOOD_SAFETY_ADHOC_SECTION_META[section])
}

const sameNum = (a, b) => a != null && b != null && Number(a) === Number(b)

/**
 * Effective temp thresholds for ad-hoc logging.
 * Keeps explicit template ranges; ambient deliveries skip defaults;
 * otherwise applies section defaults (cook ≥75°C, cool ≤8°C, delivery chilled 0–5°C).
 *
 * @param {string} section
 * @param {object|null|undefined} config
 * @returns {object}
 */
export function resolveAdHocTempConfig(section, config) {
  const base =
    config && typeof config === 'object' && !Array.isArray(config)
      ? { ...config }
      : {}
  if (base.tempBand === FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT) {
    const rest = { ...base }
    delete rest.tempMin
    delete rest.tempMax
    return { ...rest, tempBand: FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT }
  }
  // Deliveries: "no range" is never honoured, and a band with no range of its
  // own uses that band's limit (frozen is never judged against chilled).
  if (section === FOOD_SAFETY_SECTION.DELIVERIES) {
    delete base.noRange
    if (base.tempMin == null && base.tempMax == null && base.tempBand) {
      const preset = FOOD_SAFETY_DELIVERY_TEMP_PRESETS[base.tempBand]
      if (preset?.tempMax != null) return { ...base, tempMax: preset.tempMax }
    }
  }
  // Holding venue chose no range — neutral, never filled from defaults.
  if (base.noRange === true) {
    const rest = { ...base }
    delete rest.tempMin
    delete rest.tempMax
    return rest
  }
  const hasRange = base.tempMin != null || base.tempMax != null
  if (hasRange) return base
  const defaults = FOOD_SAFETY_ADHOC_TEMP_DEFAULTS[section]
  if (!defaults) return base
  return { ...base, ...defaults }
}

const bandPreset = band => FOOD_SAFETY_DELIVERY_TEMP_PRESETS[band] || null

/**
 * Range a new delivery reading is judged against — same as Express: only the
 * upper limit counts, so a minimum on an older template (0–5°C, −18 to −12°C)
 * is dropped; with no maximum left, the band's applies. Past logs keep the
 * range they were saved with (use `resolveAdHocTempConfig` for those).
 */
export function resolveNewDeliveryTempConfig(config) {
  const resolved = resolveAdHocTempConfig(
    FOOD_SAFETY_SECTION.DELIVERIES,
    config,
  )
  const rest = { ...resolved }
  delete rest.tempMin
  delete rest.noRange
  if (rest.tempBand === FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT) return rest
  if (rest.tempMax != null) return rest
  const band =
    rest.tempBand === FOOD_SAFETY_DELIVERY_TEMP_BAND.FROZEN
      ? FOOD_SAFETY_DELIVERY_TEMP_BAND.FROZEN
      : FOOD_SAFETY_DELIVERY_TEMP_BAND.CHILLED
  return { ...rest, tempMax: bandPreset(band).tempMax }
}

/** Config for judging a reading being entered now (deliveries: no minimum). */
export function resolveAdHocNewReadingConfig(section, config) {
  return section === FOOD_SAFETY_SECTION.DELIVERIES
    ? resolveNewDeliveryTempConfig(config)
    : resolveAdHocTempConfig(section, config)
}

/** "PO 1234", or the number as-is when it already starts with PO ("PO1788"). */
export function formatPurchaseOrderLabel(purchaseOrderNumber) {
  const po = String(purchaseOrderNumber || '').trim()
  if (!po) return 'PO —'
  return /^po[\s#:/-]*\w/i.test(po) ? po : `PO ${po}`
}

const formatTemp = n => `${formatTempValue(Number(n))}°C`

/** "Chilled ≤5°C" / "Frozen ≤−12°C" / "Ambient · no temp" for band options. */
export function formatDeliveryBandOptionLabel(band) {
  const preset = bandPreset(band)
  if (!preset) return ''
  if (band === FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT) {
    return `${preset.label} · no temp`
  }
  return `${preset.label} ≤${formatTemp(preset.tempMax)}`
}

/**
 * Honest range label for the composer (Chilled / Frozen / Ambient / generic).
 * Deliveries show the range a new reading is judged against.
 */
export function adHocRangeHint(section, config) {
  if (section === FOOD_SAFETY_SECTION.DELIVERIES) {
    const resolved = resolveNewDeliveryTempConfig(config)
    if (resolved.tempBand === FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT) {
      return 'Ambient — no temp range'
    }
    const max = resolved.tempMax
    if (max == null) return null
    const band = inferDeliveryTempBand(resolved)
    if (
      band &&
      band !== FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT &&
      sameNum(max, bandPreset(band).tempMax)
    ) {
      return formatDeliveryBandOptionLabel(band)
    }
    return `≤${formatTemp(max)}`
  }
  const resolved = resolveAdHocTempConfig(section, config)
  // Match Setup glance labels (≥75°C cook / ≤8°C cool) — not "Min/Max".
  const min = resolved.tempMin
  const max = resolved.tempMax
  if (min != null && max != null)
    return `Range ${formatFoodSafetyTempRange(min, max)}`
  if (min != null) return `≥${formatTempValue(min)}°C`
  if (max != null) return `≤${formatTempValue(max)}°C`
  return null
}

/** Composer escape for deliveries when using the chilled default band. */
export function adHocDeliveryRangeEscapeHint(section, config) {
  if (section !== FOOD_SAFETY_SECTION.DELIVERIES) return null
  if (config?.tempBand === FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT) {
    return 'Ambient delivery — temp is optional. Use Setup for chilled/frozen ranges.'
  }
  if (
    config?.tempBand !== FOOD_SAFETY_DELIVERY_TEMP_BAND.FROZEN &&
    sameNum(
      resolveNewDeliveryTempConfig(config).tempMax,
      bandPreset(FOOD_SAFETY_DELIVERY_TEMP_BAND.CHILLED).tempMax,
    )
  ) {
    return 'Chilled van default — change range in Setup for frozen or ambient.'
  }
  return null
}

const isBlank = value => value == null || value === ''

/**
 * Infer Setup preset from stored min/max / tempBand. Recognises today's
 * presets (max only) and older templates saved with a minimum.
 */
export function inferDeliveryTempBand(config) {
  const { AMBIENT, FROZEN, CHILLED } = FOOD_SAFETY_DELIVERY_TEMP_BAND
  if ([AMBIENT, FROZEN, CHILLED].includes(config?.tempBand)) {
    return config.tempBand
  }
  for (const band of [FROZEN, CHILLED]) {
    const preset = bandPreset(band)
    const minMatches =
      isBlank(config?.tempMin) ||
      sameNum(config?.tempMin, FOOD_SAFETY_DELIVERY_LEGACY_MINS[band])
    if (minMatches && sameNum(config?.tempMax, preset.tempMax)) return band
  }
  if (isBlank(config?.tempMin) && isBlank(config?.tempMax)) return AMBIENT
  return null
}

const nameHasWord = (name, word) =>
  new RegExp(`(^|[^a-z])${word.replace(/\s+/g, '\\s+')}([^a-z]|$)`).test(name)

/**
 * First guess at a receipt product's band from its name ("Vanilla ice cream"
 * → Frozen, "Chopped tomatoes tin" → Ambient). Chilled when nothing matches.
 */
export function guessDeliveryTempBandFromName(productName) {
  const name = String(productName || '').toLowerCase()
  const { FROZEN, AMBIENT, CHILLED } = FOOD_SAFETY_DELIVERY_TEMP_BAND
  for (const band of [FROZEN, AMBIENT]) {
    const words = FOOD_SAFETY_DELIVERY_BAND_KEYWORDS[band] || []
    if (words.some(word => nameHasWord(name, word))) return band
  }
  return CHILLED
}

export function applyDeliveryTempPreset(band) {
  const preset = FOOD_SAFETY_DELIVERY_TEMP_PRESETS[band]
  if (!preset) return null
  return {
    deliveryTempBand: band,
    tempMin: preset.tempMin,
    tempMax: preset.tempMax,
  }
}

/**
 * Temp used for out-of-range checks (value or cooling finalTemp).
 */
export function adHocTempFromDraft(section, draft) {
  if (section === FOOD_SAFETY_SECTION.COOLING) {
    return draft?.finalTemp
  }
  return draft?.value
}

/**
 * Ambient delivery templates: temp is optional (copy matches behaviour).
 * Chilled / frozen / free-type still require a reading.
 */
export function isAdHocDeliveryTempOptional(section, config) {
  if (section !== FOOD_SAFETY_SECTION.DELIVERIES) return false
  return (
    resolveAdHocTempConfig(section, config).tempBand ===
    FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT
  )
}

/** Holding venue with no range — readings show neutral, never pass/fail. */
export function isAdHocNoRange(section, config) {
  return resolveAdHocTempConfig(section, config).noRange === true
}

export function isAdHocTempOutOfRange(section, draft, config) {
  return isFridgeTempOutOfRange(
    adHocTempFromDraft(section, draft),
    resolveAdHocTempConfig(section, config),
  )
}

/**
 * Validate draft before submit. Returns error message or null.
 * Pass `config` (template config) so ambient deliveries can Accept without a temp.
 */
export function validateAdHocDraft(
  section,
  draft,
  { hasTemplate, config, coolingMode = 'oneshot' } = {},
) {
  const name = String(draft?.definitionName || '').trim()
  if (!hasTemplate && !name) {
    if (section === FOOD_SAFETY_SECTION.DELIVERIES) {
      return 'Enter a product name.'
    }
    return 'Enter a name for this log.'
  }

  if (section === FOOD_SAFETY_SECTION.COOLING) {
    if (coolingMode === 'start') {
      // Start time may be blank today — submit fills “now”.
    } else if (draft?.finalTemp == null || draft.finalTemp === '') {
      return 'Enter the final temperature.'
    }
  } else if (
    section === FOOD_SAFETY_SECTION.DELIVERIES &&
    Array.isArray(draft?.deliveryBands)
  ) {
    const dropError = validateDeliveryDrop(draft)
    if (dropError) return dropError
  } else if (
    !isAdHocDeliveryTempOptional(section, config) &&
    (draft?.value == null || draft.value === '')
  ) {
    return 'Enter a temperature.'
  }

  if (
    section === FOOD_SAFETY_SECTION.DELIVERIES &&
    !Array.isArray(draft?.deliveryBands) &&
    draft?.accepted == null
  ) {
    return 'Mark whether the delivery was accepted.'
  }

  return null
}

/**
 * Name/product-required messages should render beside the name field
 * (19.25) — not only as a top toast after temp + OOR note.
 */
export function isAdHocNameValidationError(message) {
  return /enter a (product )?name/i.test(String(message || ''))
}

/**
 * Draft patch when selecting a quick-pick template.
 * Deliveries: sync productName to the template so a prior shopping-list
 * chip cannot leave a stale name (29.18).
 */
export function adHocDraftPatchForTemplate(section, def) {
  const name = String(def?.name || '').trim()
  const patch = {
    definitionName: name,
    action: { ...EMPTY_FOOD_SAFETY_CORRECTIVE_DRAFT },
  }
  if (section === FOOD_SAFETY_SECTION.DELIVERIES) {
    patch.productName = name
    const band =
      inferDeliveryTempBand(def?.config) ||
      FOOD_SAFETY_DELIVERY_TEMP_BAND.CHILLED
    patch.deliveryBands = [{ band, value: null }]
  }
  return patch
}

/**
 * Prefill from a previous log. Name, supplier, and delivery bands come back.
 * Temperature and times stay blank so today's reading is typed again.
 * A supplier the user already typed is left alone.
 */
export function rememberedFoodSafetyDraftPatch(section, memory, draft = {}) {
  const name = String(memory?.name || '').trim()
  if (!name) return {}
  const same =
    String(draft?.definitionName || '')
      .trim()
      .toLowerCase() === name.toLowerCase()
  const patch = { definitionName: name }
  if (!same) {
    patch.value = null
    patch.finalTemp = null
    patch.startTemp = null
    patch.action = { ...EMPTY_FOOD_SAFETY_CORRECTIVE_DRAFT }
    patch.actionNudged = false
  }
  if (section === FOOD_SAFETY_SECTION.DELIVERIES) {
    patch.productName = name
    const bands = Array.isArray(memory?.bands)
      ? memory.bands.filter(Boolean)
      : []
    if (!same && bands.length) {
      patch.deliveryBands = bands.map(band => ({ band, value: null }))
    }
    const current = String(draft?.supplierName || '').trim()
    const lastAutofill = String(draft?.supplierAutofill || '').trim()
    if (!current || current === lastAutofill) {
      patch.supplierName = String(memory?.supplierName || '').trim()
      patch.supplierAutofill = ''
    }
  }
  return patch
}

/**
 * Deliveries: fill Supplier from the picked product's known supplier.
 * Never overwrites a supplier the user typed; replaces (or clears) one we
 * filled earlier. `supplierAutofill` remembers what we last filled.
 */
export function adHocSupplierAutofillPatch(draft, knownSupplier) {
  const current = String(draft?.supplierName || '').trim()
  const lastAutofill = String(draft?.supplierAutofill || '').trim()
  if (current && current !== lastAutofill) return {}
  const name = String(knownSupplier || '').trim()
  return { supplierName: name, supplierAutofill: name }
}

/** Quiet line under Supplier: where a filled value came from, or the choices. */
export function formatAdHocSupplierHint(draft, supplierOptions = []) {
  const current = String(draft?.supplierName || '').trim()
  if (current) {
    return current === String(draft?.supplierAutofill || '').trim()
      ? 'From your Shopping List. Change it if someone else delivered.'
      : ''
  }
  const options = (supplierOptions || []).filter(Boolean)
  if (options.length < 2) return ''
  const shown = options.slice(0, 3)
  const more = options.length - shown.length
  const list =
    more > 0
      ? `${shown.join(', ')} and ${more} more`
      : `${shown.slice(0, -1).join(', ')} or ${shown[shown.length - 1]}`
  return `On your Shopping List from ${list}.`
}

export const FOOD_SAFETY_DELIVERY_FREQUENT_CHIP_MAX = 10

const supplierKey = value =>
  String(value || '')
    .trim()
    .toLowerCase()

/** Last-time chips for the supplier in the box; all of them when the box is empty. */
export function filterFoodSafetyRememberedBySupplier(entries, supplierName) {
  const list = Array.isArray(entries) ? entries : []
  const want = supplierKey(supplierName)
  if (!want) return list
  return list.filter(row => supplierKey(row?.supplierName) === want)
}

/**
 * When a supplier is chosen, remembered + list chips share a cap of 10.
 * With no supplier, keep the usual last-time list and shopping-list limits.
 */
export function foodSafetyDeliveryFrequentBudget({
  rememberedCount = 0,
  supplierName = '',
  listDefaultLimit = 6,
} = {}) {
  if (!supplierKey(supplierName)) {
    return {
      rememberedLimit: Math.max(0, Number(rememberedCount) || 0),
      listLimit: listDefaultLimit,
    }
  }
  const rememberedLimit = Math.min(
    Math.max(0, Number(rememberedCount) || 0),
    FOOD_SAFETY_DELIVERY_FREQUENT_CHIP_MAX,
  )
  return {
    rememberedLimit,
    listLimit: Math.max(
      0,
      FOOD_SAFETY_DELIVERY_FREQUENT_CHIP_MAX - rememberedLimit,
    ),
  }
}

/** Clear template selection — wipe both name fields. */
export function adHocDraftPatchClearTemplate() {
  return { definitionName: '', productName: '' }
}

/**
 * Deliveries submit names. Active template always wins over a stale
 * shopping-chip productName (cannot log “Apples” under Chicken template).
 */
export function resolveAdHocDeliverySubmitNames(draft, templateDefinition) {
  if (templateDefinition) {
    const name = String(
      templateDefinition.name || draft?.definitionName || '',
    ).trim()
    return { definitionName: name, productName: name }
  }
  const definitionName = String(
    draft?.definitionName || draft?.productName || '',
  ).trim()
  const productName = String(
    draft?.productName || draft?.definitionName || '',
  ).trim()
  return { definitionName, productName }
}

/**
 * Build POST /logs body for an ad-hoc (or template-linked) log.
 * `draft.action` (panel draft): a rejected delivery's note becomes
 * `payload.rejectionReason`; otherwise the chosen action is sent as
 * `body.correctiveAction`. Legacy `draft.corrective` text still maps to
 * `payload.correctiveAction`.
 */
export function buildAdHocLogBody({
  section,
  localDate,
  draft,
  templateDefinition,
}) {
  const meta = getAdHocSectionMeta(section)
  if (!meta) {
    throw new Error(`Unsupported ad-hoc section: ${section}`)
  }

  const corrective = String(draft?.corrective || '').trim()
  const rejected = isAdHocDeliveryRejected(section, draft)
  const rejectionReason = rejected
    ? String(draft?.action?.notes || '').trim()
    : ''
  const correctiveAction = rejected
    ? null
    : buildFoodSafetyCorrectiveActionBody(draft?.action || {})
  let payload = {}

  if (section === FOOD_SAFETY_SECTION.COOLING) {
    const startOnly = draft.finalTemp == null || draft.finalTemp === ''
    if (startOnly) {
      payload = { startAt: draft.startAt }
      if (
        draft.startTemp != null &&
        draft.startTemp !== '' &&
        Number.isFinite(Number(draft.startTemp))
      ) {
        payload.startTemp = Number(draft.startTemp)
      }
      if (draft.remindAfterMinutes != null) {
        payload.remindAfterMinutes = Number(draft.remindAfterMinutes)
      }
    } else {
      // Final logged time only — never invent a fake zero-duration start===end
      // window (29.47). Optional startAt only when the draft supplies one.
      const now = new Date().toISOString()
      payload = {
        finalTemp: Number(draft.finalTemp),
        endAt: draft.endAt || now,
      }
      if (draft.startAt) payload.startAt = draft.startAt
      if (
        draft.startTemp != null &&
        draft.startTemp !== '' &&
        Number.isFinite(Number(draft.startTemp))
      ) {
        payload.startTemp = Number(draft.startTemp)
      }
    }
  } else if (section === FOOD_SAFETY_SECTION.DELIVERIES) {
    // Ambient: omit value when blank (never Number('') → NaN). Chilled/frozen
    // still require a temp via validateAdHocDraft before submit.
    // Template name wins over stale chip productName (29.18).
    const names = resolveAdHocDeliverySubmitNames(draft, templateDefinition)
    payload = {
      accepted: draft.accepted !== false,
      productName: names.productName,
    }
    if (Array.isArray(draft?.deliveryBands)) {
      payload.deliveryBands = deliveryBandsPayload(draft.deliveryBands)
      if (draft.accepted === false && draft.rejectionCode) {
        payload.rejectionCode = draft.rejectionCode
        payload.rejectionScope =
          draft.rejectionScope ||
          defaultDeliveryRejectionScope(draft.rejectionCode)
      }
    } else if (draft?.value != null && draft.value !== '') {
      payload.value = Number(draft.value)
    }
    const supplier = String(draft.supplierName || '').trim()
    if (supplier) payload.supplierName = supplier
  } else {
    // cooking_reheating, hot / cold holding (Express snapshots the venue range)
    payload = { value: Number(draft.value) }
    if (section === FOOD_SAFETY_SECTION.HOT_HOLDING && draft.heldSinceAt) {
      payload.heldSinceAt = draft.heldSinceAt
    }
  }

  if (corrective) payload.correctiveAction = corrective
  if (rejectionReason) payload.rejectionReason = rejectionReason

  const withAction = body =>
    correctiveAction ? { ...body, correctiveAction } : body

  if (templateDefinition?._id) {
    return withAction({
      definitionId: String(templateDefinition._id),
      localDate,
      payload,
    })
  }

  return withAction({
    section,
    definitionName: String(draft.definitionName || '').trim(),
    responseType: meta.responseType,
    localDate,
    payload,
  })
}

/**
 * Skip an ad-hoc quick-pick template for this operational day
 * (not cooking / not cooling / no delivery). Clears “still need a temp”
 * without inventing a fake check. Resets next day.
 */
export function buildAdHocSkipBody({ definitionId, localDate, note } = {}) {
  const id = String(definitionId || '').trim()
  if (!id) {
    throw new Error('definitionId is required to skip a quick pick')
  }
  const notes = String(note || '').trim()
  return {
    definitionId: id,
    localDate,
    payload: { skipped: true, ...(notes ? { notes } : {}) },
  }
}

/** @deprecated Prefer buildAdHocSkipBody — same payload. */
export function buildAdHocDeliverySkipBody(args) {
  return buildAdHocSkipBody(args)
}

export function isAdHocSkipLog(log) {
  return log?.payload?.skipped === true
}

const finiteTemp = value => {
  const n = Number(value)
  return Number.isFinite(n) ? n : undefined
}

export function foodSafetyLogTemplateName(log) {
  return String(log?.definitionName || '').trim()
}

export function canSaveFoodSafetyLogAsTemplate(section, log, templates = []) {
  if (
    section !== FOOD_SAFETY_SECTION.COOKING_REHEATING &&
    section !== FOOD_SAFETY_SECTION.COOLING
  ) {
    return false
  }
  if (!log?._id || isAdHocSkipLog(log) || log.definitionId) return false
  const name = foodSafetyLogTemplateName(log)
  if (!name) return false
  const key = name.toLowerCase()
  return !(templates || []).some(
    def =>
      String(def?.name || '')
        .trim()
        .toLowerCase() === key,
  )
}

/** Existing Setup definition (name + temp band) — not a second template store. */
export function buildFoodSafetyTemplateFromLog(section, log) {
  const name = foodSafetyLogTemplateName(log)
  if (!name) return null
  if (
    section !== FOOD_SAFETY_SECTION.COOKING_REHEATING &&
    section !== FOOD_SAFETY_SECTION.COOLING
  ) {
    return null
  }
  const snap = log?.configSnapshot || {}
  const defaults = FOOD_SAFETY_ADHOC_TEMP_DEFAULTS[section] || {}
  const config = {}
  if (section === FOOD_SAFETY_SECTION.COOKING_REHEATING) {
    config.cookingKind =
      snap.cookingKind === 'reheating' ? 'reheating' : 'cooking'
    const tempMin =
      snap.tempMin != null ? finiteTemp(snap.tempMin) : defaults.tempMin
    if (tempMin != null) config.tempMin = tempMin
    const tempMax = finiteTemp(snap.tempMax)
    if (tempMax != null) config.tempMax = tempMax
  } else {
    const tempMax =
      snap.tempMax != null ? finiteTemp(snap.tempMax) : defaults.tempMax
    if (tempMax != null) config.tempMax = tempMax
    const tempMin = finiteTemp(snap.tempMin)
    if (tempMin != null) config.tempMin = tempMin
  }
  return { section, name, config }
}

const formatCorrectionTemp = value => {
  const n = Number(value)
  return Number.isFinite(n) ? `${n}°C` : ''
}

const formatCorrectionWhen = at => {
  const date = new Date(at)
  if (Number.isNaN(date.getTime())) return ''
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(date)
  } catch (error) {
    return ''
  }
}

/** Lines under a corrected reading. Null when the log was never corrected. */
export function formatFoodSafetyCorrectionLines(log) {
  const correction = log?.correction
  if (!correction?.corrected) return null
  const original = correction.original || {}
  const originalValue =
    original.finalTemp != null ? original.finalTemp : original.value
  const when = formatCorrectionWhen(correction.correctedAt)
  const who = String(correction.correctedByName || '').trim()
  return {
    marker: 'Corrected',
    original:
      originalValue != null
        ? `Originally recorded as ${formatCorrectionTemp(originalValue)}`
        : null,
    by: [who ? `Corrected by ${who}` : 'Corrected', when]
      .filter(Boolean)
      .join(' · '),
    note: correction.note ? `Reason: ${correction.note}` : null,
  }
}

/** A single temperature can be corrected. Skips, probes and delivery bands cannot. */
export function canCorrectFoodSafetyLog(log) {
  if (!log?._id || isAdHocSkipLog(log)) return false
  if (log.status === 'in_progress') return false
  if (log.responseType === FOOD_SAFETY_RESPONSE_TYPE.CALIBRATION) return false
  if (log.responseType === FOOD_SAFETY_RESPONSE_TYPE.COMPLETE) return false
  if (log.payload?.boilingValue != null) return false
  const bands = log.payload?.deliveryBands
  if (
    Array.isArray(bands) &&
    bands.some(row => row?.value != null && row.value !== '')
  ) {
    return false
  }
  if (log.responseType === FOOD_SAFETY_RESPONSE_TYPE.COOLING) {
    return log.payload?.finalTemp != null || log.payload?.value != null
  }
  return log.payload?.value != null
}

/**
 * Same-day Undo Skip affordance (29.28).
 * Express also enforces same operational day — this only gates the button.
 */
export function canUndoAdHocSkipLog(
  log,
  { viewingLocalDate, todayLocalDate } = {},
) {
  if (!isAdHocSkipLog(log) || !log?._id || log.skipUndoneAt) return false
  const logDate = String(log.localDate || '')
  const viewing = String(viewingLocalDate || logDate)
  const today = String(todayLocalDate || '')
  if (!logDate || !today) return false
  return logDate === today && viewing === today
}

export function formatAdHocUndoSkipConfirmMessage(name) {
  const label = String(name || 'this quick pick').trim() || 'this quick pick'
  return `Undo for ${label}? It will show as still needed again.`
}

export function formatAdHocSkipStatusLabel() {
  return 'Not Required Today'
}

export function formatAdHocSkipHint(section, name) {
  const label = String(name || 'this').trim() || 'this'
  if (section === FOOD_SAFETY_SECTION.DELIVERIES) {
    return `No ${label} delivery today? Mark it not required so the day can finish.`
  }
  if (section === FOOD_SAFETY_SECTION.COOLING) {
    return `Not cooling ${label} today? Mark it not required so the day can finish.`
  }
  return `Not cooking or reheating ${label} today? Mark it not required so the day can finish.`
}

/**
 * Confirm copy before Skip today (29.12) — mid-service mis-tap guard.
 * @param {string|null|undefined} name
 * @returns {string}
 */
export function formatAdHocSkipConfirmMessage(name) {
  const label = String(name || '').trim() || 'this check'
  return `Mark ${label} as not required today?`
}

export function formatAdHocStillNeededSkipCue() {
  return 'Tap a needed check, or mark it not required if you’re not doing it today.'
}

/**
 * Still-needed templates first (amber), then the rest A→Z by name.
 */
export function orderAdHocQuickPickTemplates({
  templates = [],
  neededIds = null,
} = {}) {
  const needed =
    neededIds instanceof Set
      ? neededIds
      : new Set([...(neededIds || [])].map(String))
  const list = [...(templates || [])].filter(Boolean)
  const byName = (a, b) =>
    String(a?.name || '').localeCompare(String(b?.name || ''), undefined, {
      sensitivity: 'base',
    })
  const neededList = list.filter(d => needed.has(String(d._id))).sort(byName)
  const rest = list.filter(d => !needed.has(String(d._id))).sort(byName)
  return [...neededList, ...rest]
}

/**
 * Cap visible quick-pick chips; keep the selected template visible when collapsed.
 */
export function visibleAdHocQuickPickTemplates({
  templates = [],
  expanded = false,
  max = FOOD_SAFETY_QUICK_PICK_CHIP_MAX,
  activeId = null,
} = {}) {
  const list = [...(templates || [])].filter(Boolean)
  const limit = Math.max(1, Number(max) || FOOD_SAFETY_QUICK_PICK_CHIP_MAX)
  if (expanded || list.length <= limit) {
    return { visible: list, hiddenCount: 0, truncated: false }
  }
  let visible = list.slice(0, limit)
  const active = activeId != null ? String(activeId) : ''
  if (active && !visible.some(d => String(d._id) === active)) {
    const selected = list.find(d => String(d._id) === active)
    if (selected) {
      visible = [...visible.slice(0, Math.max(0, limit - 1)), selected]
    }
  }
  return {
    visible,
    hiddenCount: list.length - visible.length,
    truncated: true,
  }
}

export function formatAdHocLogLine(section, log) {
  const name = log?.definitionName || 'Log'
  const who = log?.completedByName || ''
  const time = log?.completedAt
    ? formatFoodSafetyClockTime(log.completedAt)
    : ''

  if (isAdHocSkipLog(log)) {
    const skipLabel = formatAdHocSkipStatusLabel(section)
    const note = String(log?.payload?.notes || '').trim()
    return {
      title: name,
      detail: note ? `${skipLabel} · ${note}` : skipLabel,
      tempLabel: null,
      statusLabel: skipLabel,
      extrasLabel: note || null,
      tone: 'neutral',
      colour: null,
      meta: [who, time].filter(Boolean).join(' · '),
      corrective: null,
      outOfRange: false,
      skipped: true,
    }
  }

  let rawTemp = null
  const extras = []
  if (section === FOOD_SAFETY_SECTION.COOLING) {
    rawTemp = log?.payload?.finalTemp
    extras.push(...formatCoolingLogExtras(log?.payload))
    if (log?.status === 'in_progress') extras.push('cooling')
  } else if (section === FOOD_SAFETY_SECTION.DELIVERIES) {
    const bandText = formatDeliveryBands(log?.payload)
    rawTemp = bandText ? null : log?.payload?.value
    if (bandText) extras.push(bandText)
    const rejectionText = formatDeliveryRejection(log?.payload)
    const supplier = String(log?.payload?.supplierName || '').trim()
    if (supplier) extras.push(`Supplier: ${supplier}`)
    const po = String(log?.payload?.purchaseOrderNumber || '').trim()
    if (po) extras.push(formatPurchaseOrderLabel(po))
    const accepted = log?.payload?.accepted
    if (rejectionText) extras.push(`Rejected · ${rejectionText}`)
    else if (accepted === false) extras.push('Rejected')
    else if (accepted === true) extras.push('Accepted')
  } else {
    rawTemp = log?.payload?.value
    const held = formatFoodSafetyHeldLine(log?.payload, log?.configSnapshot)
    if (held) extras.push(held)
  }

  // Recompute from section defaults when snapshot has no range — never paint
  // unsafe free-type temps green. Never trust stored outOfRange alone.
  const draftForRange =
    section === FOOD_SAFETY_SECTION.COOLING
      ? { finalTemp: rawTemp }
      : { value: rawTemp }
  const bandOutOfRange =
    section === FOOD_SAFETY_SECTION.DELIVERIES &&
    Array.isArray(log?.payload?.deliveryBands) &&
    log.payload.deliveryBands.length > 0
      ? isDeliveryDropOutOfRange(log.payload.deliveryBands)
      : null
  const outOfRange =
    bandOutOfRange != null
      ? bandOutOfRange
      : isAdHocNoRange(section, log?.configSnapshot)
      ? null
      : isAdHocTempOutOfRange(section, draftForRange, log?.configSnapshot)

  const parts = formatFoodSafetyTempParts({
    value: rawTemp,
    outOfRange,
  })
  // Cooling Today cards: "Final 8°C · Cooled" so chefs don’t read it as cook core.
  let tempLabel = parts.tempLabel
  let statusLabel = parts.statusLabel
  if (section === FOOD_SAFETY_SECTION.COOLING && tempLabel) {
    tempLabel = `Final ${tempLabel}`
    if (!statusLabel && outOfRange === false) {
      statusLabel = 'Cooled'
    }
  }
  const correction = formatFoodSafetyCorrectionLines(log)
  if (correction) {
    statusLabel = [statusLabel, correction.marker].filter(Boolean).join(' · ')
  }
  const detail = [tempLabel, statusLabel, ...extras].filter(Boolean).join(' · ')

  return {
    title: name,
    detail,
    tempLabel,
    statusLabel,
    extrasLabel: extras.join(' · ') || null,
    tone: parts.tone,
    colour: parts.colour,
    meta: [who, time].filter(Boolean).join(' · '),
    corrective: formatFoodSafetyCorrectiveNote(
      log?.payload?.correctiveAction || '',
    ),
    outOfRange: Boolean(outOfRange),
    skipped: false,
    correction,
  }
}

export function cookingKindLabel(kind) {
  if (kind === 'reheating') return 'Reheating'
  if (kind === 'cooking') return 'Cooking'
  return null
}

export { FOOD_SAFETY_RESPONSE_TYPE }
